import {Spec} from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {
  buildTableSearchResult,
  doesTableCellMatch,
  getSearchResultsForColumnNames,
  getSearchResultsForTableName,
} from 'notebook/mediocre/search/TableSearchUtil';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import RegistrationTableComponent from 'notebook/registration-tables/RegistrationTable';
import {
  RegistrationTable,
  RegistrationTableType,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {
  convertRegistrationTableToTable,
  getStoreDependenciesForRegistrationTable,
  mergeTableUpdateIntoRegistrationTable,
} from 'notebook/registration-tables/RegistrationTableNotePartUtil';
import * as RegistrationTableUtil from 'notebook/registration-tables/RegistrationTableUtil';
import TableWithMissingSchema from 'notebook/structured-tables/components/TableWithMissingSchema';
import {BasicTableProps} from 'notebook/structured-tables/StructuredTableConstants';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {isTableCollapsed} from 'notebook/tables/collapse/useTableCollapse';
import {Table, TableData} from 'notebook/tables/constants/TableConstants';
import {EvaluatedCell, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import TableUtil from 'notebook/tables/TableUtil';
import {toStringTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import * as logging from 'util/Logging';
import {MakeFieldsRequired} from 'util/Types';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import GetStageRunsWrapper from 'workflows/stage-entries/components/GetStageRunsWrapper';
import {StageRun} from 'workflows/WorkflowConstants';

function doesUserHavePermissionToViewTable(
  registrationTable: RegistrationTable
): registrationTable is RegistrationTable & {tagSchema: TagSchema} {
  return (
    registrationTable.tagSchema != null &&
    (registrationTable.tableType !== RegistrationTableType.BATCH || registrationTable.batchSchema != null)
  );
}

const REGISTRATION_TABLE_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.REGISTRATION_TABLE,
  typeClass: 'is-table',
  title: 'Registration Table',
  icon: 'note-table',
  component: {
    selectionInit: MediocrePlugins.table.selectionInit,
    selectionAtStart: MediocrePlugins.table.selectionAtStart,
    selectionAtEnd: MediocrePlugins.table.selectionAtEnd,
    required: [],
    getItemProps(item: NotePart & {item: RegistrationTable}, index: number): TableItemProps {
      return MediocrePlugins.getTableItemProps.call(this, item, index);
    },
    getIsCollapsed(
      item: NotePart & {item: RegistrationTable},
      editorProps: NoteEditorProps,
      collapsedStateById: CollapsedTableState
    ) {
      const rowCount = item.item.rows.length;
      const cellCount = rowCount * item.item.columns.length;
      return isTableCollapsed({
        collapseId: createCollapseId(editorProps.fileId, item.item.name),
        collapsedStateById,
        tableKind: null,
        rowCount,
        cellCount,
      });
    },
    getIsInaccessible(item: NotePart & {item: RegistrationTable}): boolean {
      return !doesUserHavePermissionToViewTable(item.item);
    },
    // Plugin methods are always called using Function.prototype.call,
    // so `this` refers to the editor component.
    make(
      this: NoteEditor,
      item: NotePart & {item: RegistrationTable},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: TableItemProps,
      searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      const cid = editorProps.getCid(item);
      const registrationTable: RegistrationTable = item.item;
      const {
        rows,
        columns,
        labelingStrategy,
        name,
        provenanceUuid,
        batchSchema,
        registrationRecordListId,
        isWatchingInputTable,
        tableType,
      } = registrationTable;
      const sharedProps: MakeFieldsRequired<BasicTableProps, 'tableTypeLabel'> = {
        icon: 'register',
        indentation: this.getIndentation(item),
        tableTypeLabel: 'Registration',
        tableName: name,
      };
      if (!doesUserHavePermissionToViewTable(registrationTable)) {
        return <TableWithMissingSchema {...sharedProps} />;
      }
      return (
        <GetStageRunsWrapper entryId={editorProps.fileId}>
          {(stageRuns: Array<StageRun>) => (
            <RegistrationTableComponent
              {...itemProps}
              {...sharedProps}
              editor={this}
              editorProps={editorProps}
              rows={rows}
              structuredTableColumns={columns}
              labelingStrategy={labelingStrategy}
              provenanceUuid={provenanceUuid}
              isWatchingInputTable={isWatchingInputTable}
              stageRuns={stageRuns}
              tableType={tableType}
              tagSchema={registrationTable.tagSchema}
              batchSchema={batchSchema}
              registrationRecordListId={registrationRecordListId}
              // Standard table props.
              cid={cid}
              index={index}
              mouseDownHandler={this.itemMouseDownHandler}
              mouseOverHandler={this.itemMouseOverHandler}
              updateHandler={this.itemUpdateHandler}
              {...searchResultProps}
            />
          )}
        </GetStageRunsWrapper>
      );
    },
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemToText(this: NoteEditor, item: NotePart & {item: RegistrationTable}): string {
    const fullTableMap = this.props.makeFullTableMap(this.props);
    const fullTable = fullTableMap[item.item.name];

    const computedTables = this.props.computedTables;
    if (!computedTables.hasOwnProperty(item.item.name)) {
      logging.warn('User copied registration table before evaluation was complete.');
      return TableUtil.tableEditableToText(
        fullTable,
        fullTable.table.map((row) =>
          row.map((cell) => ({value: toStringTypedValue(''), locked: false}) as EvaluatedCell)
        )
      );
    }

    const computedTable = computedTables[item.item.name];
    return TableUtil.tableEditableToText(fullTable, computedTable);
  },

  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemToHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: RegistrationTable}): JQuery {
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);

    const fullTableMap = this.props.makeFullTableMap(this.props);
    const fullTable = fullTableMap[item.item.name];

    const computedTables = this.props.computedTables;
    if (!computedTables.hasOwnProperty(item.item.name)) {
      logging.warn('User copied registration table before evaluation was complete.');
      return $(
        $.parseHTML(
          TableUtil.tableEditableToText(
            fullTable,
            fullTable.table.map((row) =>
              row.map((cell) => ({value: toStringTypedValue(''), locked: false}) as EvaluatedCell)
            )
          )
        )
      );
    }

    const computedTable = computedTables[item.item.name];
    $el.append(TableUtil.tableEditableToHTML(fullTable, computedTable));
    return $el;
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemFromHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: RegistrationTable}): NotePart | null {
    if ($el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID) !== this.props.fileId) {
      notifications.warn('Cannot paste registration tables between entries.');
      return null;
    }

    if (
      RegistrationTableUtil.getRegistrationTablesInNote(this.props.note)
        .map((registrationTable) => registrationTable.name)
        .includes(item.item.name)
    ) {
      notifications.warn('Cannot create more than one copy of the same registration table.');
      return null;
    }
    return item;
  },
  // This must stay in sync with table.name returned from getContent
  getSpreadsheetTableName({item}: NotePart & {item: RegistrationTable}): string {
    return item.name;
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  getContent(this: NoteEditor, {item}: NotePart & {item: RegistrationTable}): {table: Table} {
    const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
    const stageRuns = workflowSelector.getOrderedStageRunsForEntry(this.props.fileId);
    return {table: convertRegistrationTableToTable(item, this.props.fileId, stageRuns)};
  },

  setContent({
    item,
    table,
    isWatchingInputTable,
  }: {
    item?: RegistrationTable;
    table?: TableData;
    isWatchingInputTable?: boolean;
  }): Spec<NotePart> {
    if (isWatchingInputTable != null) {
      return {item: {$merge: {isWatchingInputTable}}};
    } else if (item) {
      return {$merge: {item}};
    } else if (table) {
      return {
        item: {
          $apply(registrationTable: RegistrationTable): RegistrationTable {
            return mergeTableUpdateIntoRegistrationTable(registrationTable, table);
          },
        },
      };
    }

    assert(false, 'setContent invoked without content to set.');
  },

  getStoreDependenciesForSpreadsheetEval(
    this: NoteEditor,
    {item}: NotePart & {item: RegistrationTable}
  ): StoreDependencyMap {
    return getStoreDependenciesForRegistrationTable(item);
  },

  *getSearchResults(
    item: NotePart,
    matcher: TextMatcher,
    searchOverFormulas: boolean,
    computedTables: OutputTableMap | null,
    isCollapsed: boolean
  ): Generator<MediocreSearchResult> {
    const registrationTable = item.item as RegistrationTable;
    // Search over the table name
    yield* getSearchResultsForTableName(matcher, registrationTable.name, item.cid);

    if (!isCollapsed) {
      // Search over the table column names
      const columnNames = registrationTable.columns.map((column) => column.name ?? '');
      yield* getSearchResultsForColumnNames(matcher, columnNames, item.cid);

      const computedTable = computedTables?.[registrationTable.name];
      assert(computedTable != null, 'Computed table not found in plugin', {
        computedTableNames: computedTables ? Object.keys(computedTables) : [],
      });
      for (const [i, row] of registrationTable.rows.entries()) {
        for (const [j, cell] of row.cells.entries()) {
          if (doesTableCellMatch(matcher, cell, computedTable[i][j], searchOverFormulas)) {
            yield buildTableSearchResult(item.cid, i, j);
          }
        }
      }
    }
  },
};

/*
 * This plugin is no longer supported, please do not further proliferate its use.
 */
export default REGISTRATION_TABLE_PLUGIN;
